"use client";

import {
  HydratedTable,
  useSupportedAssets,
  useTableActionPermissions,
  useDuplicateTable,
  useMoveAssetsToFolder,
  useSplitTreatments,
  trpc,
} from "@zapier/client";
import {
  KebabMenuItem,
  useEmitTableKebabInteractionEvent,
} from "@zapier/shared";
import { TableModal } from "./TableModals";
import { useCallback, useMemo } from "react";
import { HydratedTableAsset, HydratedVirtualTableAsset } from "@zapier/client";
import { HydratedTableWithFolder } from "../useTablesList";
import { useNotification, parseFolderId } from "@zapier/utils";
import { useTranslations } from "@zapier/i18n";
import { useParams } from "next/navigation";

interface Input {
  table: HydratedTable;
  setCurrentModal: (modal: TableModal | undefined) => void;
  setCurrentTable: (
    table:
      | HydratedTable
      | HydratedTableAsset
      | HydratedVirtualTableAsset
      | HydratedTableWithFolder
      | undefined,
  ) => void;
  onCopyToAnotherAccount?: (
    asset: HydratedTableAsset | HydratedVirtualTableAsset,
  ) => void;
}

export function useTableRow({
  table,
  setCurrentModal,
  setCurrentTable,
  onCopyToAnotherAccount,
}: Input) {
  const { isMPFEnabled } = useSupportedAssets();
  const permissions = useTableActionPermissions(table.id, table.kind);
  const { emit } = useEmitTableKebabInteractionEvent();
  const { createNotification } = useNotification();
  const t = useTranslations("table-row");
  const tAction = useTranslations("buttons");
  const { isZapAndCanvasUnifiedTrashEnabled, isTableUnifiedTrashEnabled } =
    useSplitTreatments();

  const isView = table.kind === "virtual_table";
  // Table views stay hard-delete-only; trash never applies to them.
  const isPermanentDelete =
    isView || !isZapAndCanvasUnifiedTrashEnabled || !isTableUnifiedTrashEnabled;

  // Get folderId from URL if present (route param), otherwise from table object
  const parameters = useParams();
  const folderIdFromUrl = parameters?.folder
    ? parseFolderId(String(parameters.folder))
    : undefined;
  const folderIdFromTable = (table as HydratedTableWithFolder).folder?.id;
  const folderId = folderIdFromUrl || folderIdFromTable;

  const utils = trpc.useUtils();

  const { mutate: duplicateTable, isPending: isDuplicating } =
    useDuplicateTable({ folderId });
  const { moveAssetsToFolder } = useMoveAssetsToFolder({ folderId });

  const handleRenameTable = useCallback(() => {
    setCurrentTable(table);
    setCurrentModal(TableModal.Rename);
    emit("rename", table.id);
  }, [setCurrentTable, table, setCurrentModal, emit]);

  const handleMoveTable = useCallback(() => {
    setCurrentTable(table);
    setCurrentModal(TableModal.Move);
    // emit("move", table.id); // TODO: implement events
  }, [setCurrentTable, table, setCurrentModal]);

  const handleDuplicateTable = () => {
    const duplicateName = t("duplicate-name", { tableName: table.name });

    duplicateTable(
      {
        sourceTableId: table.id,
        name: duplicateName,
        description: table?.description ?? undefined,
        toKind: table.kind,
      },
      {
        onSuccess: (data) => {
          const newTableId = data?.data?.id;

          createNotification({
            message: t("notifications.duplicate-success", {
              tableName: duplicateName,
            }),
            type: "success",
          });
          emit("duplicate", table.id);

          // If the original table is in a folder, move the duplicate to the same folder
          // Do this after showing the notification so it happens asynchronously
          if (folderId && newTableId) {
            moveAssetsToFolder(
              {
                assetIds: [`table:${newTableId}`],
                destinationFolderId: folderId,
              },
              {
                onSuccess: () => {
                  // Invalidate folder assets and lookups (will refetch when needed)
                  if (folderIdFromUrl) {
                    utils.asset.getHydratedFolderAssets.invalidate({
                      folderId: folderIdFromUrl,
                    });
                  }
                  utils.folder.foldersLookup.invalidate();
                },
                onError: () => {
                  createNotification({
                    message: t("notifications.duplicate-move-error"),
                    type: "error",
                  });
                },
              },
            );
          }
        },
        onError: () => {
          createNotification({
            message: t("notifications.duplicate-error", {
              tableName: table.name,
            }),
            type: "error",
          });
        },
      },
    );
  };

  const handleDeleteTable = useCallback(() => {
    setCurrentTable(table);
    setCurrentModal(TableModal.Delete);
    emit("delete", table.id);
  }, [setCurrentTable, table, setCurrentModal, emit]);

  const handleChangeOwner = useCallback(() => {
    setCurrentTable(table);
    setCurrentModal(TableModal.ChangeOwner);
    emit("change_owner", table.id);
  }, [setCurrentTable, table, setCurrentModal, emit]);

  const handleShareTable = useCallback(() => {
    setCurrentTable(table);
    setCurrentModal(TableModal.Share);
    emit("share", table.id);
  }, [setCurrentTable, table, setCurrentModal, emit]);

  const changeOwnerTooltipText = useMemo(() => {
    if (permissions.isLoading) {
      return "";
    }
    return permissions.hasTeams
      ? "You don't have permission to change the owner of this table"
      : "This feature is not available for personal accounts";
  }, [permissions.hasTeams, permissions.isLoading]);

  const disabledShareMessage = useMemo(() => {
    if (permissions.isLoading) {
      return;
    }
    return permissions.hasTeams
      ? undefined
      : "This feature is not available for personal accounts";
  }, [permissions.hasTeams, permissions.isLoading]);

  const menuItems = useMemo(() => {
    return [
      {
        label: "Rename",
        onClick: handleRenameTable,
        icon: "actionEdit",
        disabled: !permissions.canRename,
        tooltipText: permissions.isLoading
          ? undefined
          : "You don't have permission to rename this table",
      },
      {
        label: isView ? "Share view" : "Share table",
        icon: "personAdd",
        onClick: handleShareTable,
        disabled: !!disabledShareMessage,
        tooltipText: disabledShareMessage,
      },
      table.kind === "table" && {
        label: "Duplicate",
        onClick: handleDuplicateTable,
        icon: "actionCopy",
        disabled: !permissions.canDuplicate || isDuplicating,
        tooltipText: permissions.isLoading
          ? undefined
          : "You don't have permission to duplicate this table",
      },
      isMPFEnabled &&
        table.kind === "table" && {
          icon: "actionMoveToFolder",
          label: tAction("move-to-folder"),
          onClick: handleMoveTable,
          disabled: !permissions.canMove,
        },
      table.kind === "table" && {
        label: tAction("change-owner"),
        onClick: handleChangeOwner,
        icon: "arrowConnect",
        disabled: !permissions.canChangeOwner,
        tooltipText: changeOwnerTooltipText,
      },
      table.kind === "table" && { divider: true, label: "divider" },
      onCopyToAnotherAccount &&
        table.kind === "table" && {
          icon: "arrowExit",
          label: tAction("copy-to-another-account"),
          onClick: () => {
            onCopyToAnotherAccount(
              table as HydratedTableAsset | HydratedVirtualTableAsset,
            );
          },
        },
      onCopyToAnotherAccount &&
        table.kind === "table" && {
          divider: true,
          label: "divider",
        },
      {
        disabled: !permissions.canDelete,
        hasWarning: true,
        icon: "actionTrash",
        label: isPermanentDelete ? tAction("delete") : tAction("move-to-trash"),
        onClick: handleDeleteTable,
        tooltipText: permissions.isLoading
          ? undefined
          : t("no-delete-permission", { isView: String(isView) }),
      },
    ].filter(Boolean) as KebabMenuItem[];
  }, [
    permissions,
    table.kind,
    isView,
    isPermanentDelete,
    isDuplicating,
    disabledShareMessage,
    handleRenameTable,
    handleShareTable,
    handleDuplicateTable,
    handleMoveTable,
    handleChangeOwner,
    handleDeleteTable,
    changeOwnerTooltipText,
    isMPFEnabled,
    onCopyToAnotherAccount,
    t,
    tAction,
  ]);

  return {
    menuItems,
    disabledShareMessage,
    changeOwnerTooltipText,
    handleShareTable,
    triggerPermissionsCheck: permissions.enablePermissionsCheck,
  };
}
